import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface NerNorRequest {
    content: string;
    structure_enabled: boolean;
}

export type EntityCardType =
    | 'drug'
    | 'target'
    | 'organization'
    | 'disease'
    | 'news'
    | 'patent'
    | 'paper'
    | 'clinical_result'
    | 'clinical_trials'
    | 'drug_deal'

/**
 * 实体类型枚举
 */
export enum EntityType {
    Drug = 'Drug', // 药物
    Organization = 'Org', // 机构
    Target = 'Target', // 靶点
    Disease = 'Disease', // 适应症
    Patent = 'Patent', // 专利
    ClinicalTrial = 'Clinical_Trial', // 临床试验
    ClinicalResults = 'Clinical_Results', // 临床试验
    Structure = 'structure', // 结构
    Mechanism = 'Mechanism' // 机制
}

export interface NerNorResponse {
    data_type: EntityType // 实体类型
    end_index: number // 结束位置下标
    normalized_id: string // 实体唯一标识ID
    original_name: string // 实体原始名称
    start_index: number // 开始位置下标
}

export interface EntityApi {
    fetchNerNor(request: NerNorRequest): Promise<NerNorResponse[]>;
    fetchDrugCard(id: string, isLogin: boolean): Promise<any>;
    fetchTargetCard(id: string, isLogin: boolean): Promise<any>;
    fetchOrganizationCard(id: string, isLogin: boolean): Promise<any>;
    fetchDiseaseCard(id: string, isLogin: boolean): Promise<any>;
    fetchNewsCard(id: string, isLogin: boolean): Promise<any>;
    fetchPatentCard(id: string, isLogin: boolean): Promise<any>;
    fetchPaperCard(id: string, isLogin: boolean): Promise<any>;
    fetchCtResultCard(id: string, isLogin: boolean): Promise<any>;
    fetchClinicalTrialCard(id: string, isLogin: boolean): Promise<any>;
    fetchClinicalResultsCard(id: string, isLogin: boolean): Promise<any>;
    fetchDrugDealCard(id: string, isLogin: boolean): Promise<any>;
}

export const EntityApi = Symbol('EntityApi');

@injectable()
export class EntityApiImpl extends PsHttpService implements EntityApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0`,
            baseURLGuest: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0/guest`,
            lsBaseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/synapse-api/pharmsnap_webapi/1.0`,
            headers: {
                'X-Internal-Request': 'true',
            },
        };
    }

    async fetchNerNor(request: NerNorRequest): Promise<NerNorResponse[]> {
        return this.post<PatSnapCommonResponseData<NerNorResponse[]>>({
            url: `${this.options.baseURL}/ner-nor`,
            data: request,
        }).then((res) => {
            return res as unknown as NerNorResponse[];
        });
    }

    private getCardBaseURL(isLogin: boolean): string {
        return isLogin ? this.options.lsBaseURL : this.options.baseURLGuest;
    }

    async fetchDrugCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/drug/card/${id}` });
    }

    async fetchTargetCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/target/card/${id}` });
    }

    async fetchOrganizationCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/organization/card/${id}` });
    }

    async fetchDiseaseCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/disease/card/${id}` });
    }

    async fetchNewsCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/news/card/${id}` });
    }

    async fetchPatentCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/patent/card/${id}` });
    }

    async fetchPaperCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/paper/card/${id}` });
    }

    async fetchCtResultCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/ct_result/card/${id}` });
    }

    async fetchClinicalTrialCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/${isLogin ? 'clinical-trial' : 'clinical_trial'}/card/${id}` });
    }

    async fetchClinicalResultsCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/ct_result/card/${id}` });
    }

    async fetchDrugDealCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/drug_deal/card/${id}` });
    }
}
